import { getRandomPartialColor } from '#lib/helper/color.js';
import { generateUUID } from '#lib/helper/uuid.js';
import { deepClone } from '#lib/helper/clone.js';
import { businessStore, getBusinessLogo } from '#lib/stores/business.js';
import { t } from '#lib/stores/i18n.js';
import { DateTime } from 'luxon';
import { offerCollectionStore, offerStore } from '#lib/stores/offer.js';
import { sessionStore } from '#lib/stores/session.js';
import { derived, get } from 'svelte/store';
import {
	OfferEngineType,
	OfferSessionOrderStrategy,
	type GuestPrice,
	type Offer,
	type OfferCollection,
	type SaveOfferRequest
} from '#lib/types/generated/index.js';
import {
	getMaxCustomerDuration,
	getMinCustomerDuration,
	getSessionMaxCapacity
} from '#lib/helper/session.js';

export function getOfferLogo(offer: Offer | undefined): string | undefined {
	if (!offer) return undefined;
	return offer.logo ? offer.logo : getBusinessLogo();
}

export function toSaveOfferRequest(offer: Offer): SaveOfferRequest {
	return {
		name: offer.name,
		engine: offer.engine,
		color: offer.color,
		isActive: offer.isActive,
		bookingTimeBefore: offer.bookingTimeBefore ?? 0,
		excludeOtherOffers: offer.excludeOtherOffers,
		isPrivate: offer.isPrivate,
		maxDuration: offer.maxDuration ?? 0,
		maxPax: offer.maxPax,
		minPax: offer.minPax,
		maxBookingPerDay: offer.maxBookingPerDay,
		maxBookingPerSession: offer.maxBookingPerSession,
		minBreakTimeBetweenSession: offer.minBreakTimeBetweenSession,
		qrCodeEnabled: offer.qrCodeEnabled,
		allowFullOnlinePayment: offer.allowFullOnlinePayment,
		allowOfflinePayment: offer.allowOfflinePayment,
		allowDeposit: offer.allowDeposit,
		depositPercent: offer.depositPercent ?? 0,
		depositPrice: offer.depositPrice ?? 0,
		depositPricePerPax: offer.depositPricePerPax ?? 0,
		dammageEnabled: offer.dammageEnabled,
		depositRequiredFrom: offer.depositRequiredFrom ?? 0,
		cancellationTimeBefore: offer.cancellationTimeBefore ?? 0,
		allowGuaranteedPayment: offer.allowGuaranteedPayment,
		guaranteeRequiredFrom: offer.guaranteeRequiredFrom ?? 0,
		guaranteePercent: offer.guaranteePercent ?? 0,
		guaranteePricePerPax: offer.guaranteePricePerPax ?? 0,
		guaranteePrice: offer.guaranteePrice ?? 0,
		basePrice: offer.basePrice,
		followSessionsOrder: offer.followSessionsOrder,
		sessionOrders: offer.sessionOrders.map((so) => ({
			id: so.id,
			strategy: so.strategy,
			sessionIds: so.sessionIds
		})),
		privateShortName: offer.privateShortName,
		presentation: offer.presentation,
		shortDescription: offer.shortDescription,
		postBookingInfo: offer.postBookingInfo,
		collectionId: offer.collectionId,
		reconfirmTimeBefore: offer.reconfirmTimeBefore,
		validityDates: offer.validityDates,
		publishStart: offer.publishStart ? offer.publishStart.toISO() : null,
		publishEnd: offer.publishEnd ? offer.publishEnd.toISO() : null,
		maxFutureBookingTime: offer.maxFutureBookingTime,
		dammagePercent: offer.dammagePercent,
		dammagePrice: offer.dammagePrice,
		dammagePricePerPax: offer.dammagePricePerPax,
		logo: offer.logo
	};
}

export function getOfferDisplayName(offer: Offer): string {
	return offer.name.length <= 20 ? offer.name : offer.privateShortName || offer.name;
}

export function isSessionNameMatchingOffer(
	sessionName: string | undefined | null,
	offer: Offer | undefined | null
): boolean {
	if (!sessionName || !offer) return false;
	const normalizedSessionName = sessionName.trim().toLowerCase();
	return (
		normalizedSessionName === offer.name.trim().toLowerCase() ||
		normalizedSessionName === getOfferDisplayName(offer).trim().toLowerCase()
	);
}

export function getMinPrice(offer: Offer): number {
	const guestPrices = offer.guestPrices.filter((price) => price.archivedAt === null);
	return (
		(guestPrices.length > 0 ? Math.min(...guestPrices.map((price) => price.price)) : 0) +
		offer.basePrice
	);
}

export function getMaxPrice(offer: Offer): number {
	const guestPrices = offer.guestPrices.filter((price) => price.archivedAt === null);
	return (
		(guestPrices.length > 0 ? Math.max(...guestPrices.map((price) => price.price)) : 0) +
		offer.basePrice
	);
}

export function getOfferMinCapacity(offer: Offer): number {
	const guestPrices = offer.guestPrices.filter((price) => price.archivedAt === null);
	return Math.max(
		guestPrices.length > 0 ? Math.min(...guestPrices.map((p) => p.minPax)) : 0,
		offer.minPax
	);
}

export const getOfferMaxCapacity = derived([getSessionMaxCapacity], ([$getSessionMaxCapacity]) => {
	return (offer: Offer, maxPaxOverride: number | undefined = undefined): number => {
		return Math.min(
			maxPaxOverride ?? offer.maxPax,
			...offer.sessionOrders.map((sessionOrder) =>
				Math.max(
					...sessionOrder.sessionIds.map((id) => {
						const session = get(sessionStore).find((s) => s.id === id && !s.archivedAt);
						return session ? $getSessionMaxCapacity(session) : Infinity;
					})
				)
			)
		);
	};
});

export function getOfferMaxDuration(offer: Offer): number {
	return (
		offer.sessionOrders.reduce(
			(acc, sessionOrder) =>
				acc +
				Math.max(
					...sessionOrder.sessionIds.map((id) => {
						const session = get(sessionStore).find((s) => s.id === id && !s.archivedAt);
						return session ? getMaxCustomerDuration(session) : 0;
					})
				),
			0
		) +
		offer.minBreakTimeBetweenSession * (offer.sessionOrders.length - 1)
	);
}

export function getOfferMinDuration(offer: Offer): number {
	return (
		offer.sessionOrders.reduce(
			(acc, sessionOrder) =>
				acc +
				Math.min(
					...sessionOrder.sessionIds.map((id) => {
						const session = get(sessionStore).find((s) => s.id === id && !s.archivedAt);
						return session ? getMinCustomerDuration(session) : 0;
					})
				),
			0
		) +
		offer.minBreakTimeBetweenSession * (offer.sessionOrders.length - 1)
	);
}

export function newPrice(offer: Offer): GuestPrice {
	const maxPax = get(getOfferMaxCapacity)(offer);

	return {
		id: generateUUID(),
		name: get(t)(`offer.guestPrice.defaultName`),
		description: '',
		price: 0,
		minPax: 1,
		maxPax: maxPax,
		validMinPax: 0,
		validMaxPax: maxPax,
		archivedAt: null
	};
}

export const offerIsBookable = derived([getOfferMaxCapacity], ([$getOfferMaxCapacity]) => {
	return (offer: Offer): boolean => {
		return (
			offer.sessionOrders.length > 0 &&
			offer.minPax > 0 &&
			offer.maxPax > 0 &&
			getOfferMinCapacity(offer) <= $getOfferMaxCapacity(offer) &&
			(offer.maxDuration === null ||
				offer.sessionOrders.length <= 1 ||
				offer.maxDuration >= getOfferMinDuration(offer))
		);
	};
});

export function createNewOffer(engine: OfferEngineType = OfferEngineType.Recurrent): Offer {
	const targetSessions = get(sessionStore).filter((s) => !s.archivedAt);
	const currentOffers = get(offerStore);
	const maxSortOrder =
		currentOffers.length > 0 ? Math.max(...currentOffers.map((o) => o.sortOrder), 0) : 0;

	return {
		id: generateUUID(),
		name: get(t)(`settings.offers.new`),
		privateShortName: '',
		engine,
		isActive: true,
		sortOrder: maxSortOrder + 1,
		color: getRandomPartialColor(),
		followLogo: true,
		logo: null,
		presentation: '',
		shortDescription: '',
		sessionOrders:
			engine === OfferEngineType.Recurrent && targetSessions.length === 1
				? [
						{
							id: 0,
							orderId: 1,
							strategy: OfferSessionOrderStrategy.Or,
							sessionIds: [targetSessions[0].id]
						}
					]
				: [],
		isPrivate: false,
		minPax: 1,
		maxPax: 999,
		excludeOtherOffers: false,
		bookingTimeBefore: 0,
		maxFutureBookingTime: null,
		reconfirmTimeBefore: null,
		maxDuration: null,
		minBreakTimeBetweenSession: 0,
		validityStart: null,
		validityEnd: null,
		validityDates: null,
		publishStart: null,
		publishEnd: null,
		basePrice: 0,
		depositRequiredFrom: 1,
		depositPercent: null,
		depositPrice: null,
		depositPricePerPax: null,
		dammagePercent: null,
		dammagePrice: null,
		dammagePricePerPax: null,
		dammageEnabled: false,
		allowFullOnlinePayment: false,
		allowOfflinePayment: true,
		allowDeposit: false,
		cancellationTimeBefore: 0,
		guestPrices: [],
		followSessionsOrder: true,
		archivedAt: null,
		maxBookingPerDay: 0,
		maxBookingPerSession: 0,
		qrCodeEnabled: false,
		collectionId: get(businessStore).defaultOfferCollectionId,
		allowGuaranteedPayment: false,
		guaranteeRequiredFrom: 1,
		guaranteePercent: null,
		guaranteePricePerPax: null,
		guaranteePrice: null,
		postBookingInfo: ''
	};
}

export function createNewOfferCollection(): OfferCollection {
	const currentCollections = get(offerCollectionStore);
	return {
		id: generateUUID(),
		name: '',
		description: '',
		color: getRandomPartialColor(),
		sortOrder: -(currentCollections.length + 1),
		logo: null,
		offers: []
	};
}

export function copyOffers(offers: Offer[]): Offer[] {
	return deepClone(offers).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function isOfferPublished(offer: Offer, referenceDate: DateTime = DateTime.now()): boolean {
	if (offer.archivedAt || !offer.isActive) {
		return false;
	}
	if (offer.publishStart && offer.publishStart > referenceDate) {
		return false;
	}
	if (offer.publishEnd && offer.publishEnd < referenceDate) {
		return false;
	}
	if (offer.validityEnd && offer.validityEnd < referenceDate) {
		return false;
	}
	return true;
}

export function isPastOffer(offer: Offer, referenceDate: DateTime = DateTime.now()): boolean {
	if (offer.validityEnd && offer.validityEnd.endOf('day') < referenceDate) {
		return true;
	}
	if (offer.publishEnd && offer.publishEnd.endOf('day') < referenceDate) {
		return true;
	}
	return false;
}
